
import { Component, Model, Prop, Vue } from 'vue-property-decorator';
import { InputValidationRule } from 'vuetify';
import AsideLabel from './AsideLabel.vue';

export type StepItem =
  | LabelledItem
  | GroupItem
  | ChoicesItem
  | InputItem
  | TextAreaItem;

interface BaseItem {
  // type?: 'group' | 'boolean' | 'choices' | 'input' | 'textarea';
  type?: string;
  separator?: boolean;
}

export interface LabelledItem extends BaseItem {
  label: string;
  labelClass?: string | string[] | HashAny;
  description?: string | string[];
}

interface Cols {
  cols?: number;
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

export interface GroupItem extends Partial<LabelledItem>, Cols {
  // type: 'group';
  items: StepItem[];
}

export interface KeyItem extends Partial<LabelledItem> {
  key?: string;
  rules?: InputValidationRule[];
}

export interface ChoicesItem extends KeyItem {
  // type: 'choices';
  choices: Choice[];
  multiChoice: boolean;
  maxChoices?: number;
}

export interface Choice {
  label: string;
  value: string;
  showsOther?: boolean | StepItem;
  otherKey?: string;
}

export interface BaseInputItem extends KeyItem {
  useExternalLabel?: boolean;
  placeholder?: string;
  autofocus?: boolean;
  required?: boolean;
}

export interface InputItem extends BaseInputItem {
  // type: 'input';
  input: string;
}

export interface TextAreaItem extends BaseInputItem {
  type: 'textarea';
  rows?: number;
}

@Component({ components: { AsideLabel }, name: 'form-item' })
export default class extends Vue {
  @Model('change', { type: Object }) model!: HashAny;

  @Prop({ type: Object, required: true }) readonly item!: StepItem;

  get canRender() {
    return (
      this.showLabel ||
      this.showDescription ||
      this.isBoolean ||
      this.isInput ||
      this.isTextArea ||
      this.isChoices ||
      this.isGroup
    );
  }

  get isKeyed() {
    return !!(this.item as KeyItem).key;
  }

  get isGroup() {
    const { item } = this;
    return item.type === 'group' && Array.isArray((item as GroupItem).items);
  }

  get isInput() {
    const { item } = this;
    return this.isKeyed && (item.type === 'input' || (item as InputItem).input);
  }

  get isTextArea() {
    const { item } = this;
    return this.isKeyed && item.type === 'textarea';
  }

  get isBoolean() {
    const { item } = this;
    return this.isKeyed && item.type === 'boolean';
  }

  get isChoices() {
    const { item } = this;
    return (
      this.isKeyed &&
      (item.type === 'choices' || Array.isArray((item as ChoicesItem).choices))
    );
  }

  get isRadioGroup() {
    return this.isChoices && !(this.item as ChoicesItem).multiChoice;
  }

  get isMultiChoice() {
    return this.isChoices && (this.item as ChoicesItem).multiChoice;
  }

  get isExternalLabel() {
    return (
      (this.isInput || this.isTextArea) &&
      (this.item as InputItem | TextAreaItem).useExternalLabel === true
    );
  }

  get labelClass() {
    let classObj: HashAny = {};
    const { labelClass } = this.item;
    if (this.isBoolean || this.isInput || this.isTextArea || this.isChoices) {
      classObj['font-weight-bold'] = true;
    }
    if (typeof labelClass === 'string') {
      classObj[labelClass] = true;
    } else if (Array.isArray(labelClass)) {
      classObj = {};
      labelClass.forEach(value => {
        if (typeof value === 'string') {
          classObj[value] = true;
        }
      });
    } else if (typeof labelClass === 'object') {
      classObj = { ...labelClass };
    }
    classObj['form-item--external-label'] = this.isExternalLabel;
    return classObj;
  }

  get showLabel() {
    return (
      this.item.label &&
      (this.isExternalLabel || (!this.isInput && !this.isTextArea))
    );
  }

  get showDescription() {
    const { description } = this.item;
    return !!(Array.isArray(description) ? description.length : description);
  }

  mounted() {
    if (this.isMultiChoice) {
      const key = (this.item as KeyItem).key as string;
      if (!Array.isArray(this.model[key])) {
        this.model[key] = [];
      }
    }
  }

  getOther(choice: Choice): StepItem | undefined {
    if (!choice.showsOther) {
      return undefined;
    }
    let other: StepItem;
    if (choice.showsOther === true) {
      other = { type: 'textarea', key: choice.otherKey };
    } else {
      other = choice.showsOther;
      if (choice.otherKey && !(other as KeyItem).key) {
        other = { ...other, key: choice.otherKey };
      }
    }
    return other;
  }
}
